import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  useLatestChatExecution,
  useChatMessages,
  useChatMutations,
} from "@stockline/web";
import type {
  ChatMessage as V1ChatMessage,
  ChatLogEntry,
} from "@/app/api/types/chat-sessions";
import {
  CHAT_EXECUTION_STATUS,
  type ChatExecution,
} from "@stockline/shared/types/ai/chat-execution";
import { useFeatureFlag } from "@/query/hooks/useFeatureFlags";
import { V2_API_URL } from "@/lib/v2-api/client";
import client from "@/app/api";
import {
  isViewContextDebugEnabled,
  readViewContextSnapshot,
} from "@stockline/web";

export type ChatMode = "chat" | "statistics" | "unified";

// Stable empty-logs reference so the `currentLogs` memo (and thus `session`)
// keeps its identity while the chat is idle, rather than allocating `[]` per render.
const EMPTY_LOGS: ChatLogEntry[] = [];

/**
 * Session list — Dexie useLiveQuery, auto-updates via SSE → IndexedDB.
 */
export { useChatSessions } from "@stockline/web";

/**
 * Single session hook — reads from Dexie, mutations via SyncEngine.
 *
 * Data flow:
 * - Messages: Dexie useLiveQuery (SyncEngine handles /changes + SSE)
 * - Execution + logs: Dexie useLiveQuery
 * - Send: changeQueue.enqueue + engine.pushChanges (optimistic via storage.put)
 * - Interrupt: direct POST /api/v2/chat-executions/:id/cancel
 */
export function useChatSession(
  sessionId: string | null,
  _mode: ChatMode = "chat",
) {
  // Resolve allowed tools from feature flags. The full tool list is ONLY needed
  // to compute an allow-list when some tools are disabled — with no disabled
  // tools, `resolvedAllowedTools` is `undefined` (= all tools) and `allTools`
  // goes unused. Gate the fetch on that so mounting a chat (every BI page, now
  // that the launchpad gate is gone) doesn't eagerly hit `/tools` for nothing.
  const disabledTools = useFeatureFlag("aiDisabledTools");
  const { data: allTools } = useQuery({
    queryKey: ["tools"],
    queryFn: () => client.workflows.getTools(),
    enabled: !!disabledTools?.length,
  });
  const resolvedAllowedTools = useMemo(() => {
    if (!allTools) return undefined;
    if (!disabledTools?.length) return undefined; // undefined = all tools
    const disabled = new Set(disabledTools);
    return allTools.map((t) => t.name).filter((name) => !disabled.has(name));
  }, [allTools, disabledTools]);

  // Dexie live queries — auto-rerender on IndexedDB changes
  const v2Messages = useChatMessages(sessionId);
  const latestExecution = useLatestChatExecution(sessionId);

  // Derive processing state from execution (PENDING or RUNNING)
  const isActivelyProcessing =
    latestExecution?.status === CHAT_EXECUTION_STATUS.PENDING ||
    latestExecution?.status === CHAT_EXECUTION_STATUS.RUNNING;

  const isWaitingForInput =
    latestExecution?.status === CHAT_EXECUTION_STATUS.WAITING_INPUT;

  // Mutations via SyncEngine
  const {
    createSession,
    deleteSession,
    sendMessage,
    cancelExecution,
    flushSessions,
  } = useChatMutations();

  // Send wrapper — resumes workflow when execution is waiting for input
  const [isSending, setIsSending] = useState(false);
  const send = useCallback(
    async ({
      content,
      mediaIds,
      targetSessionId,
    }: {
      content: string;
      mediaIds?: string[];
      targetSessionId?: string;
    }) => {
      const sid = targetSessionId ?? sessionId;
      if (!sid) return;
      setIsSending(true);
      try {
        // The resume endpoint carries text only — attachments can't ride it
        // (and an empty user_response is coerced to "Confirmed" server-side,
        // which would phantom-confirm whatever the agent asked). Any send
        // with media goes through the normal message path so the agent
        // actually sees the attachment; the new turn's history includes the
        // pending question, so the reply still lands in context.
        if (isWaitingForInput && latestExecution?.id && !mediaIds?.length) {
          // Resume the paused workflow with user response.
          // The resume endpoint persists the user message server-side,
          // so we do NOT call sendMessage here to avoid duplicates.
          // Snapshot the current view at send-time too — the user may
          // have navigated/filtered while the workflow was paused, and
          // the resumed agent should see what's on screen NOW, not
          // what was on screen when it paused.
          const token = localStorage.getItem("bearer");
          const viewContext = readViewContextSnapshot();
          if (isViewContextDebugEnabled()) {
            console.log(
              "[view_context] chat resume →",
              viewContext
                ? `attached (page=${viewContext.page})`
                : "no snapshot, sending without",
            );
          }
          const res = await fetch(
            `${V2_API_URL}/api/v2/chat-executions/${latestExecution.id}/resume`,
            {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                user_response: content,
                ...(viewContext ? { viewContext } : {}),
              }),
            },
          );
          // fetch() only rejects on network errors — surface HTTP failures
          // too, so callers (e.g. the mobile composer) keep the draft for
          // retry instead of silently dropping the message.
          if (!res.ok) {
            throw new Error(`Resume failed with status ${res.status}`);
          }
        } else {
          await sendMessage(sid, content, mediaIds, resolvedAllowedTools);
        }
      } finally {
        setIsSending(false);
      }
    },
    [
      sessionId,
      sendMessage,
      resolvedAllowedTools,
      isWaitingForInput,
      latestExecution?.id,
    ],
  );

  // Interrupt wrapper
  const [isInterrupting, setIsInterrupting] = useState(false);
  const interrupt = useCallback(async () => {
    if (!latestExecution?.id) return;
    setIsInterrupting(true);
    try {
      await cancelExecution(latestExecution.id);
    } finally {
      setIsInterrupting(false);
    }
  }, [latestExecution?.id, cancelExecution]);

  // Convert V2 messages to V1 format for ChatWindow compatibility. The message
  // list keys each row on `msg.id`, so it only reconciles in place (instead of
  // remounting every MessageBubble + its media attachments) if the mapped array
  // preserves object identity for unchanged rows. Two things break that naively,
  // and both are guarded off ONE snapshot of the last committed render:
  //
  // 1. Fresh objects per write. Dexie's `toArray()` returns brand-new objects on
  //    every IndexedDB write, so a plain `.map()` allocates a new object for
  //    EVERY row on every SSE tick — new `message` prop refs defeat React.memo
  //    on MessageBubble and re-render the whole list when only one message
  //    arrived. Reuse the previously-mapped object whenever the source row's
  //    `updatedAt` (bumps on any change) is unchanged.
  //
  // 2. Transient empty reads. A live-query re-subscribe (DB reopen `epoch` bump,
  //    or a scope-invalidation wipe of `chat_messages`) momentarily yields `[]`
  //    for a session that is still open and non-empty; the adapter can't
  //    distinguish "not loaded yet" from "genuinely empty" (both surface as
  //    `[]`). Rendering that gap would tear down the whole keyed list and remount
  //    it once the re-pull lands. Retain the last non-empty mapping for the SAME
  //    session through such a gap — a real empty conversation has no prior
  //    non-empty render to keep, so its empty state still shows.
  //
  // The snapshot the two guards read from is committed in an effect (post-commit)
  // rather than mid-render, so a concurrent render that React starts and then
  // abandons can never leave stale cache/sticky state behind.
  const committedRef = useRef<{
    sessionId: string | null;
    messages: V1ChatMessage[];
    cache: Map<string, { updatedAt: string; mapped: V1ChatMessage }>;
  }>({ sessionId: null, messages: [], cache: new Map() });

  const { messages, nextCache } = useMemo(() => {
    const prevCache = committedRef.current.cache;
    const cache = new Map<
      string,
      { updatedAt: string; mapped: V1ChatMessage }
    >();
    const raw = (v2Messages ?? []).map((m) => {
      const cached = prevCache.get(m.id);
      const mapped: V1ChatMessage =
        cached && cached.updatedAt === m.updatedAt
          ? cached.mapped
          : {
              id: m.id,
              role: m.role as "user" | "assistant",
              content: m.content,
              timestamp: m.createdAt,
              tool_calls: m.toolCalls as unknown as V1ChatMessage["tool_calls"],
              tool_results:
                m.toolResults as unknown as V1ChatMessage["tool_results"],
              proposal_batch_id: m.proposalBatchId,
              execution_id: m.executionId,
            };
      cache.set(m.id, { updatedAt: m.updatedAt, mapped });
      return mapped;
    });
    const sameSession = committedRef.current.sessionId === sessionId;
    const kept =
      sameSession &&
      raw.length === 0 &&
      committedRef.current.messages.length > 0
        ? committedRef.current.messages
        : raw;
    return { messages: kept, nextCache: cache };
  }, [v2Messages, sessionId]);

  // Commit the snapshot only after React keeps this render. On a transient empty
  // read we advance neither the sticky messages nor the cache (both stay bound to
  // the last non-empty render), so identity survives the gap and the re-pull
  // reuses the same row objects; on a session switch we always advance (never
  // leak the prior thread).
  useEffect(() => {
    const switched = committedRef.current.sessionId !== sessionId;
    committedRef.current = {
      sessionId,
      messages:
        switched || messages.length > 0
          ? messages
          : committedRef.current.messages,
      cache:
        switched || nextCache.size > 0 ? nextCache : committedRef.current.cache,
    };
  }, [sessionId, messages, nextCache]);

  const currentLogs = useMemo(
    () =>
      isActivelyProcessing
        ? executionLogsToChatLogs(latestExecution)
        : EMPTY_LOGS,
    [isActivelyProcessing, latestExecution],
  );

  // Memoize the aggregate so `session` keeps a stable identity when nothing
  // changed — ChatWindow uses `session.messages`/`session.current_logs` as
  // effect deps (scroll-to-bottom), which would otherwise fire every render.
  const combinedData = useMemo(
    () =>
      sessionId
        ? {
            id: sessionId,
            messages,
            current_logs: currentLogs,
            error_message: latestExecution?.errorMessage ?? null,
          }
        : null,
    [sessionId, messages, currentLogs, latestExecution?.errorMessage],
  );

  const create = useCallback(
    (title?: string) => createSession({ title }),
    [createSession],
  );

  return {
    session: combinedData,
    isLoading: false,
    isProcessing: isActivelyProcessing,
    isWaitingForInput,
    send,
    isSending,
    interrupt,
    isInterrupting,
    create,
    flushSessions,
    isCreating: false,
    deleteSession: (id: string) => {
      deleteSession(id);
    },
    isDeleting: false,
  };
}

function executionLogsToChatLogs(
  execution: ChatExecution | null,
): ChatLogEntry[] {
  if (!execution?.logs) return [];
  return execution.logs as unknown as ChatLogEntry[];
}
